import React, { FunctionComponent, useMemo } from 'react';
import { Tv2Play } from '@tv2/ui-system/icons/next/logos/Tv2Play';
import { useUserInteractionTrack } from '@tv2/lib-offspring/hooks/tracking/useUserInteractionTrack';
import { ExternalLink } from '@tv2/lib-offspring/components/link/Link';
import { useModifications } from '@tv2-cas/offspring/admin/ModificationsProvider';
import { FooterType } from '@tv2-cas/offspring/api/footer/useFooter';
import Disclaimer from './Disclaimer';
import { iosBadge, androidBadge } from './images';

export function Footer({ footerData }: { footerData: FooterType }) {
  const trackUserInteraction = useUserInteractionTrack();
  const { footer } = useModifications(
    useMemo(
      () => ({
        footer: footerData,
      }),
      [footerData]
    )
  );
  return (
    <footer role="contentinfo" className="mt-14">
      <div className="deck flex flex-col 1440:flex-row gap-10">
        {!footer ? (
          <Skeleton />
        ) : (
          <div className="flex flex-wrap gap-2 1440:gap-0">
            {footer.categories.slice(0, 6).map(category => (
              <div
                key={category.title + category.items[0].title}
                className="576:w-col-6 w-col-5 768:w-col-2"
              >
                <div>
                  <h5 className="text-label-m my-2 p-0 text-white">{category.title}</h5>
                </div>
                {category.items.map((item, i) => {
                  return (
                    <div className="text-button-label-s mb-2 p-0 relative" key={item.title}>
                      <ExternalLink
                        tracking={item.tracking}
                        className="no-underline text-midnight-400 hover:underline hover:text-white"
                        key={`${item}`}
                        href={item.url}
                        onClick={() => {
                          trackUserInteraction({
                            data: {
                              label: `tv2play.footer.${category.title.toLowerCase()}.${item.title.toLowerCase()}`,
                            },
                          });
                        }}
                      >
                        {item.title}
                      </ExternalLink>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="deck mt-10 mb-4">
        <div className="w-col-12 flex justify-between gap-4">
          <Tv2Play className="h-10 w-20" />
          <div className="flex gap-2">
            <div>
              <a
                target="_blank"
                href="https://itunes.apple.com/dk/app/tv-2-play/id515236434?ls=1&mt=8"
                onClick={() => {
                  trackUserInteraction({
                    data: { label: 'tv2play.footer.storelinks.ios' },
                  });
                }}
              >
                <img
                  className="h-10 hover:scale-m"
                  src={iosBadge.src}
                  title="Hent TV 2 Play i App Store"
                  alt="Hent TV 2 Play i App Store"
                />
              </a>
            </div>
            <div>
              <a
                target="_blank"
                href="https://play.google.com/store/apps/details?id=dk.tv2.tv2play&hl=da"
                onClick={() => {
                  trackUserInteraction({
                    data: { label: 'tv2play.footer.storelinks.android' },
                  });
                }}
              >
                <img
                  className="h-10 hover:scale-m"
                  src={androidBadge.src}
                  title="Hent TV 2 Play på Google Play"
                  alt="Hent TV 2 Play på Google Play"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="deck">
        <div className="w-col-12 py-3">
          <Disclaimer />
        </div>
      </div>
    </footer>
  );
}

const Skeleton: FunctionComponent = () => {
  return (
    <div className="flex">
      {[...Array(4)].map((_, i) => (
        <div className="flex flex-col" key={i}>
          <div className="flex-none w-col-3 h-5 mb-2 bg-midnight-700 animate-pulse" />
          <div className="w-col-3 mb-1 flex-shrink-0 select-none">
            <div
              className="h-3 mb-1 bg-midnight-700 animate-pulse"
              style={{ animationDelay: `${i * 0.2}s` }}
            ></div>
            <div
              className="h-3 mb-1 bg-midnight-700 animate-pulse"
              style={{ animationDelay: `${i * 0.2}s` }}
            ></div>
            <div
              className="h-3 mb-1 bg-midnight-700 animate-pulse"
              style={{ animationDelay: `${i * 0.2}s` }}
            ></div>
            <div
              className="h-3 mb-1 bg-midnight-700 animate-pulse"
              style={{ animationDelay: `${i * 0.2}s` }}
            ></div>
          </div>
        </div>
      ))}
    </div>
  );
};
